import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Bug, Send, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const NAVY = '#0B1824';
const ORANGE = '#F5A623';

export default function HelpModal({ open, onOpenChange, accountId }) {
  const [view, setView] = useState('home'); // 'home' | 'bug'
  const [desc, setDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (v) => {
    if (!v) { setView('home'); setDesc(''); }
    onOpenChange(v);
  };

  const openMail = () => {
    const subject = encodeURIComponent(`SmarterHoops Support — ${accountId || 'No Account'}`);
    const mailto = `mailto:help@smarterhoops.com?subject=${subject}`;
    window.location.href = mailto;
  };

  const submitBug = async () => {
    const text = desc.trim();
    if (!text) { toast.error('Please describe the issue'); return; }
    setSubmitting(true);
    try {
      await api.post('/bug-reports', { description: text });
      toast.success('Bug report submitted — thanks!');
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="help-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[18px] uppercase tracking-[.04em]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
            {view === 'bug' && (
              <button onClick={() => setView('home')} aria-label="Back" className="mr-1" style={{ background: 'none', border: 'none' }}>
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            {view === 'bug' ? 'Report a Bug' : 'Help'}
          </DialogTitle>
        </DialogHeader>

        {view === 'home' ? (
          <div className="space-y-2 mt-2 pb-2">
            <button
              onClick={openMail}
              data-testid="help-contact-support"
              className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors hover:bg-slate-50 active:scale-[.99]"
              style={{ background: '#fff', border: '1.5px solid #DDE5EC' }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: ORANGE, color: NAVY }}>
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-bold" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>Contact Support</p>
                <p className="text-[12px]" style={{ color: '#64748B' }}>Email help@smarterhoops.com</p>
              </div>
            </button>

            <button
              onClick={() => setView('bug')}
              data-testid="help-report-bug"
              className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors hover:bg-slate-50 active:scale-[.99]"
              style={{ background: '#fff', border: '1.5px solid #DDE5EC' }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                <Bug className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-bold" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>Report a Bug</p>
                <p className="text-[12px]" style={{ color: '#64748B' }}>Send us a quick description of the issue</p>
              </div>
            </button>

            {accountId && (
              <p className="text-[11px] text-center pt-2" style={{ color: '#94A3B8' }}>
                Account ID · {accountId}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3 mt-2 pb-2">
            <label className="block text-[12px] font-bold uppercase tracking-[.10em]" style={{ color: '#64748B', fontFamily: "'Barlow Condensed', sans-serif" }}>
              Describe the issue
            </label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={5}
              placeholder="What happened? What were you trying to do?"
              data-testid="bug-description"
              className="w-full p-3 rounded-xl text-[14px] resize-none"
              style={{ border: '1.5px solid #DDE5EC', fontFamily: 'system-ui, sans-serif' }}
            />
            <button
              onClick={submitBug}
              disabled={submitting}
              data-testid="bug-submit"
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full text-[13px] font-bold uppercase tracking-[.08em] transition-all active:scale-[.97] disabled:opacity-60"
              style={{ background: NAVY, color: '#fff', fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
